import * as React from 'react';
import {useEffect, useMemo} from 'react';
import {FieldError} from 'react-hook-form';
import {DatePicker} from 'antd';
import classNames from 'classnames';
import dayjs, {Dayjs} from 'dayjs';
import {getErrorMessage} from 'helpers/utils';
import {RangeValue} from 'rc-picker/lib/interface';
import {DATE_FORMAT, DATE_TIME_FORMAT} from '../../helpers/formats';

const {RangePicker} = DatePicker;

export interface DateInputProps {
  id?: string;
  label?: string;
  value?: [Date | undefined, Date | undefined];
  disabled?: boolean;
  className?: string;
  required?: boolean;
  showTime?: boolean;
  isAllDay?: boolean;
  error?: FieldError | Array<FieldError | undefined>;
  disabledDates?: Date[];
  onChange: (startDate?: Date, endDate?: Date) => void;
}

function convertToDates(value?: RangeValue<dayjs.Dayjs>, fullDay?: boolean): [Date | undefined, Date | undefined] {
  if (fullDay) {
    return [value?.[0]?.startOf('date').toDate(), value?.[1]?.endOf('date')?.toDate()];
  }

  return [value?.[0]?.toDate(), value?.[1]?.toDate()];
}

export const isDateDisabled = (
  date: Dayjs,
  disabledDatesDayJs: Dayjs[] | undefined,
  selectedValues: {begins?: Dayjs; ends?: Dayjs} | undefined
): boolean => {
  const nextStartDay =
    selectedValues?.begins &&
    disabledDatesDayJs
      ?.filter((date) => selectedValues.begins && date > selectedValues.begins)
      .sort((a, b) => a.diff(selectedValues.begins) - b.diff(selectedValues.begins))[0];
  const prevEndDate =
    selectedValues?.ends &&
    disabledDatesDayJs
      ?.filter((date) => selectedValues?.ends && date < selectedValues.ends)
      .sort((a, b) => a.diff(selectedValues.ends) - b.diff(selectedValues.ends))[0];

  return !!(
    (nextStartDay && date > nextStartDay) ||
    (prevEndDate && date < prevEndDate) ||
    (disabledDatesDayJs && disabledDatesDayJs.some((x) => x.diff(date, 'day') === 0))
  );
};

export const RangeInput: React.FC<DateInputProps> = (props) => {
  const {
    id,
    label,
    value,
    disabled,
    className,
    required,
    error,
    showTime,
    isAllDay = false,
    disabledDates,
    onChange,
  } = props;
  const errorMessage = error ? getErrorMessage(error, label) : null;

  const [inputValue, setInputValue] = React.useState<RangeValue<Dayjs> | undefined>();
  const [selectedValues, setSelectedValues] = React.useState<{begins?: Dayjs; ends?: Dayjs} | undefined>();

  const disabledDatesDayJs: Dayjs[] = useMemo(() => {
    return (disabledDates ?? []).map((x) => dayjs(x));
  }, [disabledDates]);

  useEffect(() => {
    setInputValue([dayjs(value?.[0]), dayjs(value?.[1])]);
  }, [value]);

  const onChangeInput = (value: RangeValue<dayjs.Dayjs>) => {
    if (isAllDay) {
      onChange(...convertToDates(value, true));
    }

    onChange(...convertToDates(value));
  };

  return (
    <div className={classNames('text-input-wrapper', className)}>
      {label && (
        <label htmlFor={id}>
          {label} {required && <span>*</span>}
        </label>
      )}
      <div className="relative">
        <RangePicker
          id={id}
          className={classNames(errorMessage ? 'text-input--error' : 'text-input', disabled && 'text-antdDisabled')}
          style={{display: 'inline-flex'}}
          disabled={disabled}
          onOpenChange={(isOpen) => {
            if (isOpen && inputValue) {
              setSelectedValues(undefined);
              setInputValue([null, null]);
            }
          }}
          onCalendarChange={(values) => setSelectedValues({begins: values?.[0] as Dayjs, ends: values?.[1] as Dayjs})}
          value={inputValue}
          showTime={showTime && !isAllDay}
          format={isAllDay ? DATE_FORMAT : DATE_TIME_FORMAT}
          minuteStep={15}
          disabledDate={(date) => isDateDisabled(date, disabledDatesDayJs, selectedValues)}
          onChange={onChangeInput}
        />
      </div>
      {errorMessage && <p className="input-error-message">{errorMessage}</p>}
    </div>
  );
};
